import React, { useCallback, memo } from 'react';
import styled from 'styled-components';
import { FaDownload, FaBolt, FaIndustry, FaChartLine } from 'react-icons/fa';
import { PageHero, PageHeroInner, PageWrap, PageInner } from '../components/PageHero';

const VideoSection = styled.div`
  background: white;
  border: 1px solid #eee;
  margin-bottom: 2px;
`;

const VideoWrap = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    border: 0;
  }
`;

const VideoCaption = styled.div`
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 3px solid #03045e;

  p { color: #555; font-size: 0.95rem; line-height: 1.8; }
  span { font-size: 3rem; font-weight: 900; color: #f0f0f0; letter-spacing: -3px; flex-shrink: 0; margin-left: 1.5rem; }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 1rem;
    span { display: none; }
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  background: #eee;
  margin-bottom: 2px;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const StatBox = styled.div`
  background: #03045e;
  padding: 2.5rem;
  text-align: center;
  transition: background 0.25s ease;

  &:hover { background: #00b4d8; h3, p { color: #03045e; } }

  h3 { font-size: clamp(2rem, 3vw, 2.8rem); font-weight: 900; color: #00b4d8; letter-spacing: -2px; line-height: 1; transition: color 0.25s ease; }
  p { color: rgba(255,255,255,0.5); font-size: 0.75rem; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; margin-top: 0.5rem; transition: color 0.25s ease; }
`;

const SectionHeader = styled.div`
  padding: 2rem 2.5rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 1.5rem;

  span { font-size: 0.7rem; font-weight: 800; letter-spacing: 3px; text-transform: uppercase; color: #00b4d8; }
  h2 { font-size: 1.3rem; font-weight: 900; color: #03045e; letter-spacing: -0.5px; }

  &::after { content: ''; flex: 1; height: 1px; background: #f0f0f0; }
`;

const HowSection = styled.div`
  background: white;
  border: 1px solid #eee;
  margin-bottom: 2px;
`;

const HowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: #f0f0f0;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const HowCard = styled.div`
  background: white;
  padding: 2.5rem;
  transition: all 0.25s ease;
  position: relative;
  overflow: hidden;

  &::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 3px; background: #00b4d8; transition: width 0.35s ease; }

  &:hover {
    background: #03045e;
    &::after { width: 100%; }
    .icon { background: #00b4d8; color: #03045e; }
    h4, p { color: white; }
    p { color: rgba(255,255,255,0.55); }
  }

  .icon { width: 48px; height: 48px; background: #03045e; display: flex; align-items: center; justify-content: center; color: #00b4d8; font-size: 1.1rem; margin-bottom: 1.5rem; transition: all 0.25s ease; }
  h4 { font-size: 1rem; font-weight: 800; color: #03045e; margin-bottom: 0.8rem; transition: color 0.25s ease; }
  p { font-size: 0.88rem; color: #777; line-height: 1.8; transition: color 0.25s ease; }
`;

const BenefitSection = styled.div`
  background: #03045e;
  border: 1px solid #03045e;
  margin-bottom: 2px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const BenefitBox = styled.div`
  background: #03045e;
  padding: 2.5rem 2rem;

  @media (max-width: 768px) { padding: 2rem 1.5rem; }

  h3 { font-size: 0.72rem; font-weight: 800; letter-spacing: 3px; text-transform: uppercase; color: #00b4d8; margin-bottom: 1.5rem; }

  ul { list-style: none; display: flex; flex-direction: column; gap: 1rem;
    li { display: flex; align-items: flex-start; gap: 1rem; color: rgba(255,255,255,0.65); font-size: 0.92rem; line-height: 1.7;
      &::before { content: '→'; color: #00b4d8; font-weight: 700; flex-shrink: 0; margin-top: 1px; }
    }
  }
`;

const BigStat = styled.div`
  background: #00b4d8;
  padding: 3rem 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;

  h2 { font-size: clamp(4rem, 8vw, 7rem); font-weight: 900; color: #03045e; letter-spacing: -5px; line-height: 0.9; }
  p { color: rgba(3,4,94,0.65); font-size: 0.8rem; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; margin-top: 1rem; }
`;

const GuaranteeGrid = styled.div`
  background: #eee;
  border: 1px solid #eee;
  margin-bottom: 2px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const GuaranteeBox = styled.div`
  background: #03045e;
  padding: 2.5rem 2rem;
  min-width: 0;
  word-break: break-word;

  @media (max-width: 768px) { padding: 2rem 1.5rem; }

  h3 {
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #00b4d8;
    margin-bottom: 1.5rem;
    word-break: normal;
  }

  .big {
    font-size: clamp(2.5rem, 8vw, 4rem);
    font-weight: 900;
    color: #00b4d8;
    letter-spacing: -3px;
    line-height: 1;
  }

  .main {
    color: rgba(255,255,255,0.9);
    font-size: 0.9rem;
    margin-top: 0.8rem;
    line-height: 1.7;
  }

  .sub {
    color: rgba(255,255,255,0.45);
    font-size: 0.82rem;
    margin-top: 0.8rem;
    line-height: 1.7;
    border-top: 1px solid rgba(255,255,255,0.08);
    padding-top: 0.8rem;
  }

  .highlight { color: #00b4d8; }
`;

const DownloadSection = styled.div`
  background: #03045e;
  border: 1px solid #03045e;
  padding: 3rem 2.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;

  @media (max-width: 768px) { flex-direction: column; text-align: center; }

  div {
    h3 { font-size: 0.72rem; font-weight: 800; letter-spacing: 3px; text-transform: uppercase; color: #00b4d8; margin-bottom: 0.8rem; }
    p { color: rgba(255,255,255,0.55); font-size: 0.95rem; line-height: 1.7; }
  }
`;

const DownloadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 32px;
  background: #00b4d8;
  color: #03045e;
  border: none;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: 'Inter', sans-serif;

  &:hover { background: #90e0ef; }
`;

const HidrogenPasta = () => {
  const handleDownload = useCallback(() => {
    const link = document.createElement('a');
    link.href = '/assets/media/HIDROGENPASTA.pdf';
    link.download = 'HIDROGENPASTA.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  return (
    <>
      <PageHero data-title="PASTA">
        <PageHeroInner>
          <div className="left">
            <span>Hidrogen untuk Pembangkit Listrik</span>
            <h1>HIDROGEN<br />PASTA</h1>
            <p>Efisiensi listrik industri hingga 50% dengan garansi uang kembali 100%</p>
          </div>
          <div className="right">
            <div className="data-title">PASTA</div>
          </div>
        </PageHeroInner>
      </PageHero>

      <PageWrap>
        <PageInner wide style={{ overflowX: 'hidden' }}>

          <VideoSection>
            <VideoWrap>
              <iframe title="HidrogenPasta Video" src="https://www.youtube.com/embed/s1YTKtjVnTs"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen loading="lazy" />
            </VideoWrap>
            <VideoCaption>
              <p>Rekayasa teknologi berbasis hidrogen untuk menekan pemakaian beban rekening listrik industri dengan satuan panel RST dan berbasis manajemen distribusi panel (MDP) — efisiensi hingga 50%.</p>
              <span>01</span>
            </VideoCaption>
          </VideoSection>

          <StatsRow>
            <StatBox><h3>50%</h3><p>Efisiensi Listrik</p></StatBox>
            <StatBox><h3>RST</h3><p>Panel Industri</p></StatBox>
            <StatBox><h3>MDP</h3><p>Manajemen Distribusi</p></StatBox>
          </StatsRow>

          <HowSection>
            <SectionHeader><span>Cara Kerja</span><h2>Teknologi HIDROGENPASTA</h2></SectionHeader>
            <HowGrid>
              <HowCard>
                <div className="icon"><FaBolt /></div>
                <h4>Reduksi Beban Listrik</h4>
                <p>Teknologi hidrogen menekan konsumsi daya dari beban ke meteran secara signifikan tanpa mengurangi performa.</p>
              </HowCard>
              <HowCard>
                <div className="icon"><FaIndustry /></div>
                <h4>Panel RST Industri</h4>
                <p>Dirancang khusus untuk instalasi panel 3 fase industri besar dengan sistem distribusi yang terkelola.</p>
              </HowCard>
              <HowCard>
                <div className="icon"><FaChartLine /></div>
                <h4>Penghematan Bertahap</h4>
                <p>Efisiensi meningkat secara bertahap seiring pemakaian — semakin lama semakin hemat hingga 50%.</p>
              </HowCard>
            </HowGrid>
          </HowSection>

          <BenefitSection>
            <BenefitBox>
              <h3>Keunggulan</h3>
              <ul>
                <li>Tidak mencuri daya — efisiensi legal dan terukur</li>
                <li>Kompatibel dengan semua jenis panel industri</li>
                <li>Instalasi mudah tanpa modifikasi sistem utama</li>
                <li>Penghematan bertambah seiring waktu pemakaian</li>
                <li>Garansi unit penuh selama <strong style={{color:'#00b4d8'}}>5 tahun</strong></li>
              </ul>
            </BenefitBox>
            <BigStat>
              <h2>50%</h2>
              <p>Hemat Tagihan Listrik</p>
            </BigStat>
          </BenefitSection>

          <GuaranteeGrid>
            <GuaranteeBox>
              <h3>01 — Money Back Guarantee</h3>
              <div className="big">100%</div>
              <div className="main">Uang kembali jika efisiensi tidak terbukti setelah pemasangan.</div>
              <div className="sub">Penghematan minimal 10% langsung terasa sejak hari pertama.</div>
            </GuaranteeBox>
            <GuaranteeBox>
              <h3>02 — Legalitas Terjamin</h3>
              <div className="main" style={{marginTop: 0}}>
                Sistem murni efisiensi — <span className="highlight">bukan</span> tindakan ilegal atau pencurian listrik.
              </div>
              <div className="sub">Teknologi terverifikasi dan aman secara hukum.</div>
            </GuaranteeBox>
            <GuaranteeBox>
              <h3>03 — Durabilitas</h3>
              <div style={{display:'flex', alignItems:'baseline', gap:'0.5rem'}}>
                <span className="big">5</span>
                <span style={{color:'rgba(255,255,255,0.6)', fontSize:'1rem', fontWeight:600}}>Tahun</span>
              </div>
              <div className="main">Garansi unit dan barang penuh.</div>
              <div className="sub">Material berkualitas tinggi untuk pemakaian industri jangka panjang.</div>
            </GuaranteeBox>
          </GuaranteeGrid>

          <DownloadSection>
            <div>
              <h3>Unduh Brosur</h3>
              <p>Dapatkan informasi lengkap teknologi HIDROGENPASTA dalam format PDF.</p>
            </div>
            <DownloadBtn onClick={handleDownload}>
              <FaDownload size={14} /> Download HIDROGENPASTA
            </DownloadBtn>
          </DownloadSection>

        </PageInner>
      </PageWrap>
    </>
  );
};

export default memo(HidrogenPasta);
